import { z } from "zod";
import type { FeatureConfigSources } from "./base-feature-config.schemas.js";

const CONFIG_SCOPE_VALUES = {
  DEFAULT: "default",
  GLOBAL: "global",
  ORGANIZATION: "organization",
  CHANNEL: "channel",
  EVENT: "event",
  USER: "user",
  AB_VARIANT: "ab_variant",
} as const;

export const ConfigScopeSchema = z.enum(CONFIG_SCOPE_VALUES);

export type ConfigScope = z.infer<typeof ConfigScopeSchema>;

export const ConfigScope = {
  ...CONFIG_SCOPE_VALUES,
  isAdminLevel: (scope: ConfigScope): boolean =>
    scope === CONFIG_SCOPE_VALUES.GLOBAL ||
    scope === CONFIG_SCOPE_VALUES.ORGANIZATION ||
    scope === CONFIG_SCOPE_VALUES.CHANNEL,
  buildChannelScopeId: (channel: string, identifier: string): string =>
    `${channel}:${identifier}`,
  isUserControllable: (scope: ConfigScope): boolean =>
    scope === CONFIG_SCOPE_VALUES.EVENT ||
    scope === CONFIG_SCOPE_VALUES.USER ||
    scope === CONFIG_SCOPE_VALUES.DEFAULT,
} as const;

export const CONFIG_SCOPE_PRIORITY: readonly ConfigScope[] = [
  CONFIG_SCOPE_VALUES.DEFAULT,
  CONFIG_SCOPE_VALUES.GLOBAL,
  CONFIG_SCOPE_VALUES.ORGANIZATION,
  CONFIG_SCOPE_VALUES.CHANNEL,
  CONFIG_SCOPE_VALUES.EVENT,
  CONFIG_SCOPE_VALUES.USER,
  CONFIG_SCOPE_VALUES.AB_VARIANT,
] as const;

/**
 * Highest-priority scope across `sources.enabled` + `sources.config.*`.
 *
 * "Effective" because it identifies the scope that ultimately determined the
 * resolved config: if every field falls back to defaults, returns `DEFAULT`;
 * if at least one field is contributed by an `EVENT`-level override, returns
 * `EVENT`; etc. The admin UI uses this to label each row with where its
 * value comes from in the cascade — distinct from `scopeType` on the DTO,
 * which carries the request-context scope of the lookup itself.
 *
 * The `FeatureConfigSources` import is type-only (erased at compile time) so
 * the type-level reference back into `base-feature-config.types` doesn't
 * introduce a runtime module cycle.
 */
export function getEffectiveOverrideScope(
  sources: FeatureConfigSources,
): ConfigScope {
  let best = sources.enabled;
  let bestRank = CONFIG_SCOPE_PRIORITY.indexOf(best);
  for (const scope of Object.values(sources.config)) {
    const rank = CONFIG_SCOPE_PRIORITY.indexOf(scope);
    if (rank > bestRank) {
      best = scope;
      bestRank = rank;
    }
  }
  return best;
}
